import {
  DataModelsCustomFieldValue,
  DataModelsCustomFieldValueWithOptions,
  ListOrderLines,
  ListOrderLinesResponse,
  OrdersDtoOrderLineDto,
  SearchProductsResponse,
  TAnyValue,
} from "@springtree/eva-services-core";

import Eva from "~/services/eva";
import Logger from "~/services/logger/logger";
import CrossCountry from "~/types/cross-country";

import { KIKOApplicationConfiguration } from "../../global";
import Constants from "./constants";
import invariant from "./invariant";
import productUtils from "./product-utils";

const evaUtils = {
  enrichProductSearchResponse(response: SearchProductsResponse) {
    if (!response.Products) {
      return response;
    }

    const allDiscounts = response.Options?.PrefiguredDiscounts?.filter((d) => d.PromotionLabel).map((d) => ({
      DiscountID: d.DiscountID,
      PromotionLabel: d.PromotionLabel!,
      EligibleProductIDs: d.EligibleProductIDs,
    }));

    for (const product of response.Products) {
      const availability = response.Options?.AvailabilityResult?.[product.product_id as string];
      if (availability) {
        product.is_available = availability?.Delivery?.IsAvailable || false;
        product.has_stock = availability?.Delivery?.HasStock || false;
      }

      const discounts = allDiscounts?.filter((d) => !!d.EligibleProductIDs?.find((id) => id == product.product_id));
      product.discounts = discounts as TAnyValue;
    }

    // Remove these objects because the data has already been used to enrich the products.
    delete response.Options?.PrefiguredDiscounts;
    delete response.Options?.AvailabilityResult;

    return response;
  },
  async enrichProductSearchResponseWithChildren(eva: Eva, response: SearchProductsResponse) {
    response = this.enrichProductSearchResponse(response)!;

    if (!response.Products) {
      return response as Omit<SearchProductsResponse, "Products"> & { Products?: CrossCountry.Product[] };
    }

    const parents = response.Products.filter((p) => p.logical_level === "root");
    const parentIds = parents.map((p) => p.product_id);

    if (parents.length > 0) {
      const children = await evaUtils.getChildrenProductsWithAvailability(eva, parentIds as string[]);
      const partitioned = productUtils.partitionChildrenProductsByParent(children as any);

      for (const product of response.Products) {
        (product.children as any) = partitioned[product.product_id as string];
      }
    }

    return response as Omit<SearchProductsResponse, "Products"> & { Products?: CrossCountry.Product[] };
  },

  /**
   * Used internally in the util
   * @param eva EVA instance
   * @param parentIds array of parent IDs
   * @returns children products with availability
   */
  async getChildrenProductsWithAvailability(eva: Eva, parentIds: string[]) {
    const options = {
      Options: {
        IncludePrefigureDiscounts: true,
        AvailabilityOptions: {
          Delivery: {
            AvailabilityDate: true,
          },
        },
      },
    };

    const searchProducts = eva.getChildrenProducts(parentIds, 1, Constants.MAXIMUM_PAGE_SIZE, options);
    const response = this.enrichProductSearchResponse(await searchProducts);
    const numberOfPages = Math.ceil(response.Total! / Constants.MAXIMUM_PAGE_SIZE);

    let products = response.Products ?? [];

    for (let page = 2; page < numberOfPages + 1; page++) {
      const pageResponse = await eva.getChildrenProducts(parentIds, page, Constants.MAXIMUM_PAGE_SIZE, options);
      const richResponse = this.enrichProductSearchResponse(pageResponse);

      products = products.concat(richResponse.Products ?? []);
    }

    return products;
  },

  async getListOrderLines(
    listOrderLines: (requestPayload?: ListOrderLines) => Promise<ListOrderLinesResponse | undefined>,
    orderId: string,
    options?: Omit<ListOrderLines, "OrderID" | "PageConfig">
  ): Promise<OrdersDtoOrderLineDto[]> {
    const firstResponse = await listOrderLines({
      OrderID: orderId,
      ...options,
    });

    invariant(!!firstResponse, `Failed to fetch order lines for order ${orderId}`);

    const firstResult = firstResponse.Result!;
    let lines: OrdersDtoOrderLineDto[] = firstResult.Page ?? [];
    const numberOfPages = firstResult.NumberOfPages!;
    const limit = firstResult.Limit!;
    let start = firstResult.Offset! + limit;

    for (let page = 2; page <= numberOfPages; page++) {
      const response = await listOrderLines({
        OrderID: orderId,
        ...options,
        PageConfig: {
          Limit: limit,
          Start: start,
        },
      });

      if (!response) {
        Logger.instance.error(`Empty response while fetching order lines for order ${orderId}, page ${page}`);
        continue;
      }

      start = response.Result?.Offset! + limit;
      lines = lines.concat(response.Result?.Page ?? []);
    }

    return lines.filter((line) => !line.IsCancelled);
  },

  /**
   * Used internally in the util
   * @param eva EVA instance
   * @param parentIds array of parent IDs
   * @returns children products
   */
  async getChildrenProducts(eva: Eva, parentIds: string[]) {
    const response = await eva.getChildrenProducts(parentIds, 1, Constants.MAXIMUM_PAGE_SIZE);
    const numberOfPages = Math.ceil(response.Total! / Constants.MAXIMUM_PAGE_SIZE);

    let products = response.Products ?? [];

    for (let page = 2; page < numberOfPages + 1; page++) {
      const pageResponse = await eva.getChildrenProducts(parentIds, page, Constants.MAXIMUM_PAGE_SIZE);

      products = products.concat(pageResponse.Products ?? []);
    }
    return products;
  },

  async getProductDetailWithChildren(eva: Eva, product: any, parentId?: string) {
    let root: any | undefined;
    let selected: any | undefined;
    let children: any[] | undefined;

    invariant(!!product, `product should be set when getting its children`);

    if (product.logical_level === "color" && parentId) {
      // We have fetched a specific color. Retrieve information about the parent.
      selected = product;

      const [rootResult, childrenResult] = await Promise.all([
        eva.getProductDetail(parentId),
        evaUtils.getChildrenProducts(eva, [parentId]),
      ]);

      root = rootResult.Result;
      children = childrenResult;
    } else if (product.logical_level === "root") {
      // We have fetched the root. Retrieve its children.
      root = product;

      children = await evaUtils.getChildrenProducts(eva, [root.product_id]);
      selected = (await eva.getProductDetail(children[0].product_id)).Result;
    } else {
      // We have fetched a product with no hierarchy.
      root = product;
      selected = product;
      children = [];
    }

    return { root, children, selected };
  },

  async getColorVariationsSlugs(eva: Eva) {
    const variantsMap = new Map<string, Pick<CrossCountry.Product, "product_id" | "slug" | "display_value">[]>();
    let page = 1;

    try {
      const response = await eva.getGroupedProductSlugs(page);
      let products = response?.Products ?? [];

      while (products.length) {
        for (const product of products) {
          const parentId =
            (product.logical_level_hierarchy as any)?.find((e: { name: string }) => e.name === "root")?.product_id ??
            product.product_id;

          if (!variantsMap.has(parentId)) {
            variantsMap.set(parentId, []);
          }
          const { product_id, slug, display_value } = product;
          variantsMap
            .get(parentId)!
            .push({ product_id: product_id as string, slug: slug as string, display_value: display_value as string });
        }

        products = (await eva.getGroupedProductSlugs(++page))?.Products ?? [];
      }

      return Array.from(variantsMap.values()).flat();
    } catch (error) {
      Logger.instance.error("getColorVariationsSlugs failed", error);
      return [];
    }
  },

  async getProductsByIdsWithChildren(
    eva: Eva,
    ids: string[],
    sort = Eva.DEFAULT_CATEGORY_SORT,
    page = 1,
    pageSize = Constants.DEFAULT_PAGE_SIZE
  ) {
    const base = await eva.getProductsByIds(ids, sort, page, pageSize);
    return this.enrichProductSearchResponseWithChildren(eva, base);
  },
  async getProductsByCategoryWithChildren(
    eva: Eva,
    category: string,
    sort = Eva.DEFAULT_CATEGORY_SORT,
    page = 1,
    pageSize = Constants.DEFAULT_PAGE_SIZE
  ) {
    const base = await eva.getProductsByCategory(category, sort, page, pageSize);
    return this.enrichProductSearchResponseWithChildren(eva, base);
  },
  async getProductsByQueryWithChildren(
    eva: Eva,
    query: string,
    sort = Eva.DEFAULT_CATEGORY_SORT,
    page = 1,
    pageSize = Constants.DEFAULT_PAGE_SIZE
  ) {
    const base = await eva.getProductsByQuery(query, sort, page, pageSize);
    return this.enrichProductSearchResponseWithChildren(eva, base);
  },
  async getProductsBySetWithChildren(
    eva: Eva,
    set: string,
    sort = Eva.DEFAULT_CATEGORY_SORT,
    page = 1,
    pageSize = Constants.DEFAULT_PAGE_SIZE
  ) {
    const base = await eva.getProductsBySet(set, sort, page, pageSize);
    return this.enrichProductSearchResponseWithChildren(eva, base);
  },
  getCustomFieldConfiguration(backendId: string, configuration: KIKOApplicationConfiguration) {
    const allCustomFields = Object.values(configuration.ExtendedCustomFields).flat();
    return allCustomFields.find((cf) => cf.BackendID === backendId);
  },
  getCustomFieldValueForResource(
    backendId: string,
    resource?: { CustomFieldValuesWithOptions?: { [key: string]: DataModelsCustomFieldValueWithOptions } },
    configuration?: KIKOApplicationConfiguration
  ) {
    if (!configuration) {
      return;
    }

    const customField = this.getCustomFieldConfiguration(backendId, configuration);

    if (!customField) {
      return undefined;
    }

    const customFieldValue = resource?.CustomFieldValuesWithOptions?.[customField.CustomFieldID];

    if (!customFieldValue?.Value) {
      if (customFieldValue?.Options?.DefaultCustomFieldValue) {
        const defaultKey = Object.keys(customFieldValue?.Options.DefaultCustomFieldValue);
        return customFieldValue?.Options.DefaultCustomFieldValue[defaultKey[0] as keyof DataModelsCustomFieldValue];
      } else {
        return undefined;
      }
    }

    switch (customField.DataType) {
      case "String":
      case "Text":
      case "Enum":
        return customFieldValue.Value.StringValue;
      case "Date":
        return customFieldValue.Value.DateTimeValue;
      case "Bool":
        return customFieldValue.Value.BoolValue;
      case "Integer":
      case "Decimal":
        return customFieldValue.Value.NumberValue;
      default:
        Logger.instance.warn(`Unexpected custom field type: ${customField.DataType}`);
        return undefined;
    }
  },
  getCustomFieldEnumValueForResource(
    backendId: string,
    resource: { CustomFieldValuesWithOptions?: { [key: string]: DataModelsCustomFieldValueWithOptions } },
    configuration: KIKOApplicationConfiguration
  ) {
    const customField = this.getCustomFieldConfiguration(backendId, configuration);

    if (!customField?.EnumValues) {
      return undefined;
    }

    const customFieldValue = resource.CustomFieldValuesWithOptions?.[customField.CustomFieldID];

    if (!customFieldValue?.Value) {
      return undefined;
    }

    return customField.EnumValues[customFieldValue.Value.StringValue as string];
  },
};

export default evaUtils;
